import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Navigate, useSearchParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CalendarClock,
  Edit,
  Filter,
  ImagePlus,
  ImageOff,
  LogOut,
  Palette,
  RotateCcw,
  Send,
  PlusSquare,
  RefreshCcw,
  Settings,
  Trash2,
  User,
  FileText,
  Plus,
  Download,
  CheckCircle,
  Clock,
  X,
  Award,
  LayoutDashboard,
  Activity,
  Home,
  MessageSquare,
} from 'lucide-react';
import companyLogo from '../assets/images/cc.png';
import AdminCertificates from './AdminCertificates';
import AdminContactEnquiries from './AdminContactEnquiries';
import { clearAdminSession, getAdminToken, getBackendUrl } from '../utils/auth';
const initialForm = {
  platform: 'Instagram',
  caption: '',
  hashtags: '',
  scheduledAt: '',
};

const initialFilters = {
  platform: 'all',
  status: 'all',
  search: '',
  from: '',
  to: '',
};

const AdminSocialPost = () => {
  const backendUrl = useMemo(() => getBackendUrl(), []);
  const [formData, setFormData] = useState(initialForm);
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [filters, setFilters] = useState(initialFilters);
  const [posts, setPosts] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    totalPages: 1,
    totalCount: 0,
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const activePanel = searchParams.get('tab') || 'overview';
  const setActivePanel = (tab) => setSearchParams({ tab });
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [editingPostId, setEditingPostId] = useState('');
  const [uiTheme, setUiTheme] = useState('ocean');
  const [adminProfile, setAdminProfile] = useState({
    name: 'Admin User',
    email: 'admin@clientcodeai.in',
    role: 'Super Admin',
  });
  const [settingsState, setSettingsState] = useState({
    autoRefresh: false,
    emailAlerts: true,
    compactView: false,
  });

  const initialInvoiceForm = {
    clientName: '',
    clientEmail: '',
    invoiceNumber: `INV-${Date.now().toString().slice(-6)}`,
    issueDate: new Date().toISOString().slice(0, 10),
    dueDate: '',
    items: [{ description: '', quantity: 1, rate: 0 }],
    tax: 0,
    notes: '',
  };
  const [invoices, setInvoices] = useState([]);
  const [internshipsCount, setInternshipsCount] = useState(0);
  const [siteVisitors, setSiteVisitors] = useState(0);
  const [invoiceForm, setInvoiceForm] = useState(initialInvoiceForm);
  const [invoiceView, setInvoiceView] = useState('list'); // 'list' | 'create' | 'preview'
  const [previewInvoice, setPreviewInvoice] = useState(null);

  const adminToken = getAdminToken();

  const themeMap = {
    ocean: {
      panelBackground: 'linear-gradient(145deg, rgba(15, 23, 42, 0.92), rgba(30, 41, 59, 0.75))',
      border: '1px solid rgba(99, 102, 241, 0.2)',
      chip: 'rgba(99, 102, 241, 0.12)',
    },
    emerald: {
      panelBackground: 'linear-gradient(145deg, rgba(6, 39, 36, 0.92), rgba(15, 87, 78, 0.72))',
      border: '1px solid rgba(16, 185, 129, 0.28)',
      chip: 'rgba(16, 185, 129, 0.18)',
    },
    sunset: {
      panelBackground: 'linear-gradient(145deg, rgba(53, 24, 13, 0.92), rgba(120, 53, 15, 0.72))',
      border: '1px solid rgba(249, 115, 22, 0.28)',
      chip: 'rgba(249, 115, 22, 0.2)',
    },
  };


  const getMediaSrc = (mediaUrl = '') => {
    if (!mediaUrl) return '';
    if (mediaUrl.startsWith('http://') || mediaUrl.startsWith('https://')) return mediaUrl;
    return `${backendUrl}${mediaUrl}`;
  };

  const savePreferences = async (payload) => {
    const response = await fetch(`${backendUrl}/api/admin/preferences`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${adminToken}`,
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Unable to save preferences');
    }

    if (data?.data?.profile) setAdminProfile(data.data.profile);
    if (data?.data?.settings) setSettingsState(data.data.settings);
    if (data?.data?.theme) setUiTheme(data.data.theme);

    return data;
  };

  const fetchPreferences = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/preferences`, {
        headers: {
          Authorization: `Bearer ${adminToken}`,
        },
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Unable to fetch preferences');
      }

      if (data?.data?.profile) setAdminProfile(data.data.profile);
      if (data?.data?.settings) setSettingsState(data.data.settings);
      if (data?.data?.theme) setUiTheme(data.data.theme);
    } catch (requestError) {
      if (requestError.message.toLowerCase().includes('token')) {
        clearAdminSession();
      }
      // Preferences are non-critical — don't block the UI with a hard error
    }
  };

  const fetchInternshipsCount = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/internship/all`, {
        headers: {
          Authorization: `Bearer ${adminToken}`,
        },
      });
      const data = await response.json();
      if (data.success && Array.isArray(data.registrations)) {
        const approvedCount = data.registrations.filter(r => r.status === 'Approved').length;
        setInternshipsCount(approvedCount);
      }
    } catch (error) {
      console.error('Error fetching internships count:', error);
    }
  };

  const fetchSiteVisitors = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/visitors-count`, {
        headers: {
          Authorization: `Bearer ${adminToken}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setSiteVisitors(data.count || 0);
      }
    } catch (error) {
      console.error('Error fetching site visitors count:', error);
    }
  };

  const fetchPosts = async (nextPage = pagination.page, nextLimit = pagination.limit) => {
    const safePage = Math.max(1, Number.parseInt(String(nextPage), 10) || 1);
    const safeLimit = Math.max(1, Number.parseInt(String(nextLimit), 10) || pagination.limit);

    setListLoading(true);
    setError('');

    try {
      const params = new URLSearchParams();
      params.set('page', String(safePage));
      params.set('limit', String(safeLimit));
      if (filters.platform && filters.platform !== 'all') params.set('platform', filters.platform);
      if (filters.status && filters.status !== 'all') params.set('status', filters.status);
      if (filters.search.trim()) params.set('search', filters.search.trim());
      if (filters.from) params.set('from', filters.from);
      if (filters.to) params.set('to', filters.to);

      const response = await fetch(`${backendUrl}/api/admin/social-posts?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${adminToken}`,
        },
      });

      const payload = await response.json();
      if (!response.ok || !payload.success) {
        throw new Error(payload.message || 'Unable to fetch posts');
      }

      setPosts(Array.isArray(payload.data) ? payload.data : []);
      setPagination({
        page: payload?.pagination?.page || safePage,
        limit: payload?.pagination?.limit || safeLimit,
        totalPages: payload?.pagination?.totalPages || 1,
        totalCount: payload?.totalCount || 0,
      });
    } catch (requestError) {
      if (requestError.message.toLowerCase().includes('token')) {
        clearAdminSession();
      }

      const msg = requestError.message === 'Failed to fetch'
        ? 'Server is waking up, please wait a moment and try again.'
        : requestError.message;
      setError(msg);
    } finally {
      setListLoading(false);
    }
  };

  const resetPostForm = () => {
    setFormData(initialForm);
    setSelectedImage(null);
    setEditingPostId('');
    setShowCreatePost(false);
  };

  useEffect(() => {
    if (!adminToken) {
      return;
    }

    try {
      const tokenPayload = JSON.parse(atob(adminToken.split('.')[1] || ''));
      if (tokenPayload?.email) {
        setAdminProfile((prev) => ({ ...prev, email: tokenPayload.email }));
      }
    } catch (_error) {
      // Keep fallback admin profile values if token payload parsing fails.
    }

    fetchPreferences();
    fetchPosts(1, pagination.limit);
    fetchInternshipsCount();
    fetchSiteVisitors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminToken]);

  useEffect(() => {
    if (!settingsState.autoRefresh || !adminToken) {
      return undefined;
    }

    const timer = setInterval(() => {
      fetchPosts(pagination.page, pagination.limit);
    }, 20000);

    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsState.autoRefresh, adminToken, filters, pagination.page, pagination.limit]);

  if (!adminToken) {
    return <Navigate to="/admin/login" replace />;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      const payloadData = new FormData();
      payloadData.append('platform', formData.platform);
      payloadData.append('caption', formData.caption);
      payloadData.append('hashtags', formData.hashtags);
      payloadData.append('scheduledAt', formData.scheduledAt);
      if (selectedImage) {
        payloadData.append('image', selectedImage);
      }

      const method = editingPostId ? 'PUT' : 'POST';
      const endpoint = editingPostId
        ? `${backendUrl}/api/admin/social-posts/${editingPostId}`
        : `${backendUrl}/api/admin/social-posts`;

      const response = await fetch(endpoint, {
        method,
        headers: {
          Authorization: `Bearer ${adminToken}`,
        },
        body: payloadData,
      });

      const payload = await response.json();
      if (!response.ok || !payload.success) {
        throw new Error(payload.message || 'Unable to save social post');
      }

      setMessage(payload.message || (editingPostId ? 'Social media post updated successfully' : 'Social media post saved successfully'));
      resetPostForm();
      await fetchPosts(1, pagination.limit);
    } catch (requestError) {
      if (requestError.message.toLowerCase().includes('token')) {
        clearAdminSession();
      }

      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditPost = (post) => {
    setShowCreatePost(true);
    setActivePanel('posts');
    setEditingPostId(post._id);
    setFormData({
      platform: post.platform || 'Instagram',
      caption: post.caption || '',
      hashtags: Array.isArray(post.hashtags) ? post.hashtags.join(', ') : '',
      scheduledAt: post.scheduledAt ? new Date(post.scheduledAt).toISOString().slice(0, 16) : '',
    });
    setSelectedImage(null);
    setMessage('Editing selected post. Update fields and click update.');
    setError('');
  };

  const handleDeletePost = async (postId) => {
    const isConfirmed = window.confirm('Are you sure you want to delete this post?');
    if (!isConfirmed) return;

    setError('');
    setMessage('');

    try {
      const response = await fetch(`${backendUrl}/api/admin/social-posts/${postId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${adminToken}`,
        },
      });

      const payload = await response.json();
      if (!response.ok || !payload.success) {
        throw new Error(payload.message || 'Unable to delete post');
      }

      if (editingPostId === postId) {
        resetPostForm();
      }

      setMessage(payload.message || 'Post deleted successfully.');
      await fetchPosts(pagination.page, pagination.limit);
    } catch (requestError) {
      if (requestError.message.toLowerCase().includes('token')) {
        clearAdminSession();
      }
      setError(requestError.message);
    }
  };

  const handleRestorePost = async (postId) => {
    setError('');
    setMessage('');

    try {
      const response = await fetch(`${backendUrl}/api/admin/social-posts/${postId}/restore`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${adminToken}`,
        },
      });

      const payload = await response.json();
      if (!response.ok || !payload.success) {
        throw new Error(payload.message || 'Unable to restore post');
      }

      setMessage(payload.message || 'Post restored successfully.');
      await fetchPosts(pagination.page, pagination.limit);
    } catch (requestError) {
      if (requestError.message.toLowerCase().includes('token')) {
        clearAdminSession();
      }
      setError(requestError.message);
    }
  };

  const handleAdminLogout = () => {
    clearAdminSession();
    window.location.href = '/';
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#030712] flex flex-col overflow-hidden text-slate-100 font-sans admin-dashboard">
      {/* Header */}
      <header className="h-[72px] shrink-0 z-20 flex items-center justify-between px-8" style={{
        background: "linear-gradient(135deg, rgba(6, 11, 31, 0.95), rgba(10, 16, 41, 0.9))",
        backdropFilter: "blur(24px) saturate(180%)",
        WebkitBackdropFilter: "blur(24px) saturate(180%)",
        borderBottom: "1px solid rgba(0, 210, 255, 0.2)",
        boxShadow: "0 4px 30px rgba(0, 0, 0, 0.3), 0 0 20px rgba(0, 210, 255, 0.05)"
      }}>
        <div className="flex items-center gap-3">
          <div className="relative">
            <img src={companyLogo} alt="CLIENTCODE AI" className="h-10 w-10 object-contain shrink-0 relative z-10 bg-slate-900/60 rounded-xl p-1" />
            <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-[#00D2FF] to-[#7C4DFF] blur-md opacity-50" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-white tracking-wide">CLIENTCODE AI</h1>
            <span className="text-[10px] font-semibold tracking-wider text-[#00D2FF] uppercase block -mt-0.5">Admin Panel</span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2 px-4 py-2 text-sm font-medium !text-white hover:!text-[#00D2FF] bg-[#00D2FF]/5 hover:bg-[#00D2FF]/15 border border-[#00D2FF]/30 hover:border-[#00D2FF]/60 rounded-xl transition-all duration-300 no-underline">
            <Home className="w-4 h-4 text-[#00D2FF]" />
            <span>Back to Home</span>
          </Link>
          <button type="button" className="flex items-center gap-2 px-4 py-2 text-sm font-medium !text-rose-400 hover:!text-rose-300 bg-rose-500/5 hover:bg-rose-500/15 border border-rose-500/20 hover:border-rose-500/40 rounded-xl transition-all duration-300" onClick={handleAdminLogout}>
            <LogOut className="w-4 h-4" />
            <span>Logout</span>
          </button>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <aside className="w-[270px] shrink-0 flex flex-col justify-between border-r border-slate-800/80 z-10 shadow-[4px_0_24px_rgba(0,0,0,0.2)] animate-fade-in" style={{
          background: "linear-gradient(180deg, rgba(6, 11, 31, 0.95), rgba(8, 12, 33, 0.95))",
          backdropFilter: "blur(20px)",
        }}>
          {/* Sidebar Navigation Links */}
          <div className="px-4 py-6 space-y-1.5 overflow-y-auto flex-1">
            <div className="px-3 mb-4">
              <p className="text-[10px] font-bold tracking-[0.2em] text-[#00D2FF]/85 uppercase">Management</p>
            </div>

            <button
              type="button"
              onClick={() => setActivePanel('overview')}
              className={`w-full flex items-center px-4 py-3 rounded-xl text-sm transition-all group ${
                activePanel === 'overview'
                  ? 'bg-[#00D2FF]/10 text-white font-semibold border-l-4 border-[#00D2FF]'
                  : 'text-slate-300 hover:bg-[#00D2FF]/5 hover:text-white border-l-4 border-transparent'
              }`}
            >
              <LayoutDashboard className={`w-5 h-5 mr-3 transition-colors ${activePanel === 'overview' ? 'text-[#00D2FF]' : 'text-slate-400 group-hover:text-[#00D2FF]'}`} />
              Overview
            </button>

            <button
              type="button"
              onClick={() => {
                setActivePanel('posts');
              }}
              className={`w-full flex items-center px-4 py-3 rounded-xl text-sm transition-all group ${
                activePanel === 'posts'
                  ? 'bg-[#00D2FF]/10 text-white font-semibold border-l-4 border-[#00D2FF]'
                  : 'text-slate-300 hover:bg-[#00D2FF]/5 hover:text-white border-l-4 border-transparent'
              }`}
            >
              <PlusSquare className={`w-5 h-5 mr-3 transition-colors ${activePanel === 'posts' ? 'text-[#00D2FF]' : 'text-slate-400 group-hover:text-[#00D2FF]'}`} />
              Post Panel
            </button>

            <button
              type="button"
              onClick={() => setActivePanel('internships')}
              className={`w-full flex items-center px-4 py-3 rounded-xl text-sm transition-all group ${
                activePanel === 'internships'
                  ? 'bg-[#00D2FF]/10 text-white font-semibold border-l-4 border-[#00D2FF]'
                  : 'text-slate-300 hover:bg-[#00D2FF]/5 hover:text-white border-l-4 border-transparent'
              }`}
            >
              <Award className={`w-5 h-5 mr-3 transition-colors ${activePanel === 'internships' ? 'text-[#00D2FF]' : 'text-slate-400 group-hover:text-[#00D2FF]'}`} />
              Internships
            </button>

            <button
              type="button"
              onClick={() => setActivePanel('contact-enquiries')}
              className={`w-full flex items-center px-4 py-3 rounded-xl text-sm transition-all group ${
                activePanel === 'contact-enquiries'
                  ? 'bg-[#00D2FF]/10 text-white font-semibold border-l-4 border-[#00D2FF]'
                  : 'text-slate-300 hover:bg-[#00D2FF]/5 hover:text-white border-l-4 border-transparent'
              }`}
            >
              <MessageSquare className={`w-5 h-5 mr-3 transition-colors ${activePanel === 'contact-enquiries' ? 'text-[#00D2FF]' : 'text-slate-400 group-hover:text-[#00D2FF]'}`} />
              Contact Enquiries
            </button>

            <button
              type="button"
              onClick={() => { setActivePanel('invoice'); setInvoiceView('list'); }}
              className={`w-full flex items-center px-4 py-3 rounded-xl text-sm transition-all group ${
                activePanel === 'invoice'
                  ? 'bg-[#00D2FF]/10 text-white font-semibold border-l-4 border-[#00D2FF]'
                  : 'text-slate-300 hover:bg-[#00D2FF]/5 hover:text-white border-l-4 border-transparent'
              }`}
            >
              <FileText className={`w-5 h-5 mr-3 transition-colors ${activePanel === 'invoice' ? 'text-[#00D2FF]' : 'text-slate-400 group-hover:text-[#00D2FF]'}`} />
              Invoices
            </button>

            <button
              type="button"
              onClick={() => setActivePanel('salary-slip')}
              className={`w-full flex items-center px-4 py-3 rounded-xl text-sm transition-all group ${
                activePanel === 'salary-slip'
                  ? 'bg-[#00D2FF]/10 text-white font-semibold border-l-4 border-[#00D2FF]'
                  : 'text-slate-300 hover:bg-[#00D2FF]/5 hover:text-white border-l-4 border-transparent'
              }`}
            >
              <CalendarClock className={`w-5 h-5 mr-3 transition-colors ${activePanel === 'salary-slip' ? 'text-[#00D2FF]' : 'text-slate-400 group-hover:text-[#00D2FF]'}`} />
              Salary Slips
            </button>

            <div className="px-3 pt-4 mb-2">
              <p className="text-[10px] font-bold tracking-[0.2em] text-[#00D2FF]/85 uppercase">Configuration</p>
            </div>

            <button
              type="button"
              onClick={() => setActivePanel('profile')}
              className={`w-full flex items-center px-4 py-3 rounded-xl text-sm transition-all group ${
                activePanel === 'profile'
                  ? 'bg-[#00D2FF]/10 text-white font-semibold border-l-4 border-[#00D2FF]'
                  : 'text-slate-300 hover:bg-[#00D2FF]/5 hover:text-white border-l-4 border-transparent'
              }`}
            >
              <User className={`w-5 h-5 mr-3 transition-colors ${activePanel === 'profile' ? 'text-[#00D2FF]' : 'text-slate-400 group-hover:text-[#00D2FF]'}`} />
              Admin Profile
            </button>

            <button
              type="button"
              onClick={() => setActivePanel('settings')}
              className={`w-full flex items-center px-4 py-3 rounded-xl text-sm transition-all group ${
                activePanel === 'settings'
                  ? 'bg-[#00D2FF]/10 text-white font-semibold border-l-4 border-[#00D2FF]'
                  : 'text-slate-300 hover:bg-[#00D2FF]/5 hover:text-white border-l-4 border-transparent'
              }`}
            >
              <Settings className={`w-5 h-5 mr-3 transition-colors ${activePanel === 'settings' ? 'text-[#00D2FF]' : 'text-slate-400 group-hover:text-[#00D2FF]'}`} />
              Settings
            </button>

            <button
              type="button"
              onClick={() => setActivePanel('theme')}
              className={`w-full flex items-center px-4 py-3 rounded-xl text-sm transition-all group ${
                activePanel === 'theme'
                  ? 'bg-[#00D2FF]/10 text-white font-semibold border-l-4 border-[#00D2FF]'
                  : 'text-slate-300 hover:bg-[#00D2FF]/5 hover:text-white border-l-4 border-transparent'
              }`}
            >
              <Palette className={`w-5 h-5 mr-3 transition-colors ${activePanel === 'theme' ? 'text-[#00D2FF]' : 'text-slate-400 group-hover:text-[#00D2FF]'}`} />
              UI Theme
            </button>
          </div>

          {/* Sidebar Footer */}
          <div className="p-4 border-t border-slate-800/80 bg-slate-950/20 space-y-2">
            <Link to="/" className="w-full flex items-center px-4 py-3 rounded-xl text-sm font-medium transition-all text-slate-300 hover:bg-[#00D2FF]/10 hover:text-white no-underline group">
              <Home className="w-5 h-5 mr-3 text-slate-400 group-hover:text-[#00D2FF]" />
              Back to Home
            </Link>

            <button
              type="button"
              onClick={handleAdminLogout}
              className="w-full flex items-center px-4 py-3 rounded-xl text-sm font-medium transition-all text-rose-400 hover:bg-rose-500/10 hover:text-rose-300"
            >
              <LogOut className="w-5 h-5 mr-3 text-rose-500/70" />
              Logout
            </button>
          </div>
        </aside>

        {/* Main Content Area */}
        <main className="flex-1 overflow-y-auto p-[30px] bg-[#F8FAFC]">
          {error ? <div className="bg-red-50 text-red-600 px-4 py-3 rounded-xl mb-6 shadow-sm border border-red-100">{error}</div> : null}
          {message ? <div className="bg-green-50 text-green-600 px-4 py-3 rounded-xl mb-6 shadow-sm border border-green-100">{message}</div> : null}

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full"
          >
              {activePanel === 'overview' ? (
                <div className="space-y-6">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-2xl font-bold text-[#111827]">Dashboard Overview</h2>
                      <p className="text-[#6B7280] text-sm mt-1">Welcome back, {adminProfile.name}! Here's what's happening.</p>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {[
                      { title: 'Total Posts', value: pagination.totalCount || 0, icon: PlusSquare, color: 'text-blue-600', bg: 'bg-blue-50' },
                      { title: 'Invoices Issued', value: invoices.length || 0, icon: FileText, color: 'text-purple-600', bg: 'bg-purple-50' },
                      { title: 'Active Internships', value: internshipsCount, icon: Award, color: 'text-green-600', bg: 'bg-green-50' },
                      { title: 'Site Visitors', value: siteVisitors, icon: Activity, color: 'text-orange-600', bg: 'bg-orange-50' }
                    ].map((stat, index) => (
                      <div key={index} className="bg-white p-6 rounded-2xl border border-[#E5E7EB] shadow-[0_2px_8px_rgba(0,0,0,0.04)] flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${stat.bg}`}>
                          <stat.icon className={`w-6 h-6 ${stat.color}`} />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-[#6B7280]">{stat.title}</p>
                          <h4 className="text-2xl font-bold text-[#111827] mt-1">{stat.value}</h4>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="grid lg:grid-cols-2 gap-6">
                    <div className="bg-white p-6 rounded-2xl border border-[#E5E7EB] shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
                      <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-bold text-[#111827]">Quick Actions</h3>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <button onClick={() => { setActivePanel('posts'); setShowCreatePost(true); }} className="p-4 border border-[#E5E7EB] rounded-xl flex flex-col items-center justify-center gap-3 hover:border-[#7C4DFF] hover:bg-[#F3EEFF] transition-all group">
                          <PlusSquare className="w-6 h-6 text-[#9CA3AF] group-hover:text-[#7C4DFF]" />
                          <span className="text-sm font-medium text-[#4B5563] group-hover:text-[#7C4DFF]">New Post</span>
                        </button>
                        <button onClick={() => { setActivePanel('invoice'); setInvoiceView('create'); }} className="p-4 border border-[#E5E7EB] rounded-xl flex flex-col items-center justify-center gap-3 hover:border-[#7C4DFF] hover:bg-[#F3EEFF] transition-all group">
                          <FileText className="w-6 h-6 text-[#9CA3AF] group-hover:text-[#7C4DFF]" />
                          <span className="text-sm font-medium text-[#4B5563] group-hover:text-[#7C4DFF]">New Invoice</span>
                        </button>
                        <button onClick={() => setActivePanel('internships')} className="p-4 border border-[#E5E7EB] rounded-xl flex flex-col items-center justify-center gap-3 hover:border-[#7C4DFF] hover:bg-[#F3EEFF] transition-all group">
                          <Award className="w-6 h-6 text-[#9CA3AF] group-hover:text-[#7C4DFF]" />
                          <span className="text-sm font-medium text-[#4B5563] group-hover:text-[#7C4DFF]">Manage Interns</span>
                        </button>
                        <button onClick={() => setActivePanel('settings')} className="p-4 border border-[#E5E7EB] rounded-xl flex flex-col items-center justify-center gap-3 hover:border-[#7C4DFF] hover:bg-[#F3EEFF] transition-all group">
                          <Settings className="w-6 h-6 text-[#9CA3AF] group-hover:text-[#7C4DFF]" />
                          <span className="text-sm font-medium text-[#4B5563] group-hover:text-[#7C4DFF]">Settings</span>
                        </button>
                      </div>
                    </div>
                    
                    <div className="bg-white p-6 rounded-2xl border border-[#E5E7EB] shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
                      <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-bold text-[#111827]">Recent Activity</h3>
                        <button className="text-sm text-[#7C4DFF] font-medium hover:underline">View All</button>
                      </div>
                      <div className="space-y-5">
                        {[
                          { text: 'Admin updated their profile', time: '2 hours ago', icon: User, bg: 'bg-blue-50', color: 'text-blue-600' },
                          { text: 'New post scheduled for Instagram', time: '5 hours ago', icon: CalendarClock, bg: 'bg-purple-50', color: 'text-purple-600' },
                          { text: 'Generated invoice INV-847291', time: '1 day ago', icon: FileText, bg: 'bg-green-50', color: 'text-green-600' }
                        ].map((activity, i) => (
                          <div key={i} className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${activity.bg}`}>
                              <activity.icon className={`w-5 h-5 ${activity.color}`} />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-[#1F2937]">{activity.text}</p>
                              <p className="text-xs text-[#6B7280] mt-0.5">{activity.time}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}

              {activePanel === 'posts' ? (
                <div className="space-y-4">
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                    <h2 className="text-2xl font-bold text-[#111827]">Post Management</h2>
                    <div className="flex gap-3">
                      <button type="button" className="bg-gradient-to-r from-[#7C4DFF] to-[#9B6DFF] text-white px-5 py-2.5 rounded-xl font-medium hover:shadow-lg transition-all flex items-center gap-2" onClick={() => setShowCreatePost(true)}>
                        <PlusSquare className="w-5 h-5" />
                        Create Post
                      </button>
                    </div>
                  </div>

                  <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-[0_6px_20px_rgba(0,0,0,0.06)] p-6 space-y-5">
                    <h3 className="text-lg font-bold text-[#111827]">All Posts With Filter</h3>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <select
                          value={filters.platform}
                          onChange={(event) => setFilters((prev) => ({ ...prev, platform: event.target.value }))}
                          className="w-full h-[46px] px-4 rounded-xl bg-white border border-[#D1D5DB] text-[#1F2937] focus:border-[#7C4DFF] focus:ring-1 focus:ring-[#7C4DFF] outline-none transition-shadow"
                        >
                          <option value="all">All Platforms</option>
                          <option value="Instagram">Instagram</option>
                          <option value="LinkedIn">LinkedIn</option>
                          <option value="Facebook">Facebook</option>
                          <option value="Twitter/X">Twitter/X</option>
                          <option value="YouTube">YouTube</option>
                        </select>
                        <select
                          value={filters.status}
                          onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value }))}
                          className="w-full h-[46px] px-4 rounded-xl bg-white border border-[#D1D5DB] text-[#1F2937] focus:border-[#7C4DFF] focus:ring-1 focus:ring-[#7C4DFF] outline-none transition-shadow"
                        >
                          <option value="all">All Active</option>
                          <option value="upcoming">Upcoming</option>
                          <option value="published">Published</option>
                          <option value="trashed">Trashed</option>
                        </select>
                        <input
                          type="date"
                          value={filters.from}
                          onChange={(event) => setFilters((prev) => ({ ...prev, from: event.target.value }))}
                          className="w-full h-[46px] px-4 rounded-xl bg-white border border-[#D1D5DB] text-[#1F2937] focus:border-[#7C4DFF] focus:ring-1 focus:ring-[#7C4DFF] outline-none transition-shadow"
                        />
                        <input
                          type="date"
                          value={filters.to}
                          onChange={(event) => setFilters((prev) => ({ ...prev, to: event.target.value }))}
                          className="w-full h-[46px] px-4 rounded-xl bg-white border border-[#D1D5DB] text-[#1F2937] focus:border-[#7C4DFF] focus:ring-1 focus:ring-[#7C4DFF] outline-none transition-shadow"
                        />
                      </div>
                      <div className="flex gap-3">
                        <input
                          type="text"
                          value={filters.search}
                          onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
                          placeholder="Search caption/hashtags"
                          className="flex-1 h-[46px] px-4 rounded-xl bg-white border border-[#D1D5DB] text-[#1F2937] focus:border-[#7C4DFF] focus:ring-1 focus:ring-[#7C4DFF] outline-none transition-shadow"
                        />
                        <button type="button" className="bg-white border border-[#D1D5DB] text-[#4B5563] px-5 py-2.5 rounded-xl font-medium hover:bg-gray-50 transition-all flex items-center gap-2" onClick={() => fetchPosts(1, pagination.limit)}>
                          <Filter className="w-5 h-5" />
                          Apply
                        </button>
                        <button
                          type="button"
                          className="bg-white border border-[#D1D5DB] text-[#EF4444] px-5 py-2.5 rounded-xl font-medium hover:bg-red-50 transition-all flex items-center gap-2"
                          onClick={() => {
                            setFilters(initialFilters);
                            setTimeout(() => fetchPosts(1, pagination.limit), 0);
                          }}
                        >
                          <RefreshCcw className="w-5 h-5" />
                          Reset
                        </button>
                      </div>

                      <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-[#6B7280]">
                        <p>Total Posts: <span className="font-bold text-[#111827]">{pagination.totalCount}</span></p>
                        <div className="flex items-center gap-2">
                          <span>Per page</span>
                          <select
                            value={pagination.limit}
                            onChange={(event) => {
                              const nextLimit = Number(event.target.value);
                              setPagination((prev) => ({ ...prev, limit: nextLimit, page: 1 }));
                              fetchPosts(1, nextLimit);
                            }}
                            className="px-3 py-1.5 rounded-lg bg-white border border-[#D1D5DB] text-[#1F2937] outline-none"
                          >
                            <option value={8}>8</option>
                            <option value={12}>12</option>
                            <option value={16}>16</option>
                          </select>
                        </div>
                      </div>

                      <div className={`grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 max-h-[620px] overflow-auto pr-1 ${settingsState.compactView ? 'text-xs' : ''}`}>
                        {listLoading ? <p className="text-[#6B7280] text-sm">Loading posts...</p> : null}
                        {!listLoading && posts.length === 0 ? <p className="text-[#6B7280] text-sm">No posts found.</p> : null}
                        {posts.map((post) => (
                          <article
                            key={post._id}
                            className="bg-white rounded-2xl border border-[#E5E7EB] shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-all p-5 min-h-[380px] flex flex-col"
                          >
                            <div className="flex items-center justify-between gap-3 mb-4">
                              <span className="text-xs px-3 py-1.5 rounded-full bg-[#F3EEFF] text-[#7C4DFF] font-medium border border-[#E9D5FF]">
                                {post.platform}
                              </span>
                              <span className="text-[11px] text-[#6B7280] font-medium">
                                {new Date(post.scheduledAt).toLocaleString()}
                              </span>
                            </div>
                            <p className="text-sm text-[#1F2937] mb-3 leading-relaxed line-clamp-3 flex-1">{post.caption}</p>
                            {Array.isArray(post.hashtags) && post.hashtags.length ? (
                              <p className="text-xs text-[#7C4DFF] mb-3 line-clamp-1">{post.hashtags.join(', ')}</p>
                            ) : null}
                            
                            <div className="flex flex-wrap gap-1.5 mb-4">
                              <span className="text-[10px] px-2 py-0.5 rounded border border-[#E5E7EB] bg-[#F9FAFB] text-[#6B7280]">
                                Updated by: {post.updatedBy || post.createdBy || 'admin'}
                              </span>
                            </div>
                            
                            {post.mediaUrl ? (
                              <img
                                src={getMediaSrc(post.mediaUrl)}
                                alt="post media"
                                className="w-full h-44 object-cover rounded-xl border border-[#E5E7EB]"
                              />
                            ) : (
                              <div className="w-full h-44 rounded-xl border border-dashed border-[#D1D5DB] bg-[#F9FAFB] flex flex-col items-center justify-center text-[#9CA3AF] gap-2">
                                <ImageOff className="w-6 h-6" />
                                <p className="text-xs font-medium">No image attached</p>
                              </div>
                            )}
                            
                            <div className="flex items-center gap-2 mt-4 pt-4 border-t border-[#F3F4F6]">
                              {post.isDeleted ? (
                                <button
                                  type="button"
                                  className="flex-1 bg-white border border-[#D1D5DB] text-[#4B5563] px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 flex items-center justify-center gap-1.5"
                                  onClick={() => handleRestorePost(post._id)}
                                >
                                  <RotateCcw className="w-4 h-4" />
                                  Restore
                                </button>
                              ) : (
                                <>
                                  <button
                                    type="button"
                                    className="flex-1 bg-white border border-[#D1D5DB] text-[#4B5563] px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 flex items-center justify-center gap-1.5"
                                    onClick={() => handleEditPost(post)}
                                  >
                                    <Edit className="w-4 h-4" />
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    className="flex-1 bg-white border border-[#FCA5A5] text-[#EF4444] px-3 py-2 rounded-lg text-sm font-medium hover:bg-red-50 flex items-center justify-center gap-1.5"
                                    onClick={() => handleDeletePost(post._id)}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                    Trash
                                  </button>
                                </>
                              )}
                            </div>
                          </article>
                        ))}
                      </div>

                      <div className="flex items-center justify-between pt-4 border-t border-[#F3F4F6]">
                        <button
                          type="button"
                          className="bg-white border border-[#D1D5DB] text-[#4B5563] px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={pagination.page <= 1}
                          onClick={() => fetchPosts(pagination.page - 1, pagination.limit)}
                        >
                          Previous
                        </button>
                        <p className="text-sm font-medium text-[#4B5563]">
                          Page {pagination.page} of {pagination.totalPages}
                        </p>
                        <button
                          type="button"
                          className="bg-white border border-[#D1D5DB] text-[#4B5563] px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={pagination.page >= pagination.totalPages}
                          onClick={() => fetchPosts(pagination.page + 1, pagination.limit)}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                </div>
              ) : null}

              {activePanel === 'profile' ? (
                <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-[0_6px_20px_rgba(0,0,0,0.06)] p-6 space-y-5">
                  <h2 className="text-xl font-bold text-[#111827]">Admin Profile</h2>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-[#4B5563] mb-1.5">Admin Name</label>
                      <input
                        type="text"
                        value={adminProfile.name}
                        onChange={(event) => setAdminProfile((prev) => ({ ...prev, name: event.target.value }))}
                        placeholder="John Doe"
                        className="w-full h-[46px] px-4 rounded-xl bg-white border border-[#D1D5DB] text-[#1F2937] focus:border-[#7C4DFF] focus:ring-1 focus:ring-[#7C4DFF] outline-none transition-shadow"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#4B5563] mb-1.5">Role</label>
                      <input
                        type="text"
                        value={adminProfile.role}
                        onChange={(event) => setAdminProfile((prev) => ({ ...prev, role: event.target.value }))}
                        placeholder="Super Admin"
                        className="w-full h-[46px] px-4 rounded-xl bg-white border border-[#D1D5DB] text-[#1F2937] focus:border-[#7C4DFF] focus:ring-1 focus:ring-[#7C4DFF] outline-none transition-shadow"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#4B5563] mb-1.5">Email Address</label>
                    <input
                      type="email"
                      value={adminProfile.email}
                      onChange={(event) => setAdminProfile((prev) => ({ ...prev, email: event.target.value }))}
                      placeholder="admin@clientcode.ai"
                      className="w-full h-[46px] px-4 rounded-xl bg-white border border-[#D1D5DB] text-[#1F2937] focus:border-[#7C4DFF] focus:ring-1 focus:ring-[#7C4DFF] outline-none transition-shadow"
                    />
                  </div>
                  <div className="pt-2">
                    <button
                      type="button"
                      className="bg-gradient-to-r from-[#7C4DFF] to-[#9B6DFF] text-white px-6 py-3 rounded-xl font-medium hover:shadow-lg hover:-translate-y-[1px] transition-all"
                      onClick={async () => {
                        setError('');
                        try {
                          await savePreferences({
                            profile: { name: adminProfile.name, role: adminProfile.role },
                          });
                          setMessage('Admin profile updated successfully.');
                        } catch (requestError) {
                          if (requestError.message.toLowerCase().includes('token')) {
                            clearAdminSession();
                          }
                          setError(requestError.message);
                        }
                      }}
                    >
                      Save Profile
                    </button>
                  </div>
                </div>
              ) : null}

              {activePanel === 'settings' ? (
                <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-[0_6px_20px_rgba(0,0,0,0.06)] p-6 space-y-5">
                  <h2 className="text-xl font-bold text-[#111827]">Settings</h2>
                  <label className="flex items-center justify-between rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] px-5 py-4 text-[#4B5563] font-medium hover:border-[#D1D5DB] transition-colors cursor-pointer">
                    Auto Refresh Post List
                    <input
                      type="checkbox"
                      checked={settingsState.autoRefresh}
                      onChange={(event) => setSettingsState((prev) => ({ ...prev, autoRefresh: event.target.checked }))}
                      className="w-5 h-5 text-[#7C4DFF] rounded border-[#D1D5DB] focus:ring-[#7C4DFF]"
                    />
                  </label>
                  <label className="flex items-center justify-between rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] px-5 py-4 text-[#4B5563] font-medium hover:border-[#D1D5DB] transition-colors cursor-pointer">
                    Email Alerts
                    <input
                      type="checkbox"
                      checked={settingsState.emailAlerts}
                      onChange={(event) => setSettingsState((prev) => ({ ...prev, emailAlerts: event.target.checked }))}
                      className="w-5 h-5 text-[#7C4DFF] rounded border-[#D1D5DB] focus:ring-[#7C4DFF]"
                    />
                  </label>
                  <label className="flex items-center justify-between rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] px-5 py-4 text-[#4B5563] font-medium hover:border-[#D1D5DB] transition-colors cursor-pointer">
                    Compact View
                    <input
                      type="checkbox"
                      checked={settingsState.compactView}
                      onChange={(event) => setSettingsState((prev) => ({ ...prev, compactView: event.target.checked }))}
                      className="w-5 h-5 text-[#7C4DFF] rounded border-[#D1D5DB] focus:ring-[#7C4DFF]"
                    />
                  </label>
                  <div className="pt-2">
                    <button
                      type="button"
                      className="bg-gradient-to-r from-[#7C4DFF] to-[#9B6DFF] text-white px-6 py-3 rounded-xl font-medium hover:shadow-lg hover:-translate-y-[1px] transition-all"
                      onClick={async () => {
                        setError('');
                        try {
                          await savePreferences({ settings: settingsState });
                          setMessage('Settings saved successfully.');
                        } catch (requestError) {
                          if (requestError.message.toLowerCase().includes('token')) {
                            clearAdminSession();
                          }
                          setError(requestError.message);
                        }
                      }}
                    >
                      Save Settings
                    </button>
                  </div>
                </div>
              ) : null}

              {activePanel === 'theme' ? (
                <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-[0_6px_20px_rgba(0,0,0,0.06)] p-6 space-y-5">
                  <h2 className="text-xl font-bold text-[#111827]">UI Theme</h2>
                  <p className="text-sm text-[#6B7280]">
                    Select a theme (Note: In this enterprise redesign, the dashboard layout is fixed, but theme saving logic remains intact).
                  </p>
                  <div className="grid sm:grid-cols-3 gap-4">
                    {Object.keys(themeMap).map((themeKey) => (
                      <button
                        key={themeKey}
                        type="button"
                        onClick={() => setUiTheme(themeKey)}
                        className={`rounded-xl px-5 py-4 text-sm font-medium capitalize transition-all border ${
                          uiTheme === themeKey
                            ? 'border-[#7C4DFF] bg-[#F3EEFF] text-[#7C4DFF] shadow-sm'
                            : 'border-[#E5E7EB] text-[#4B5563] hover:border-[#D1D5DB] hover:bg-[#F9FAFB]'
                        }`}
                      >
                        {themeKey}
                      </button>
                    ))}
                  </div>
                  <p className="text-sm font-medium text-[#4B5563]">Selected theme: <span className="text-[#7C4DFF] capitalize font-bold">{uiTheme}</span></p>
                  <div className="pt-2">
                    <button
                      type="button"
                      className="bg-gradient-to-r from-[#7C4DFF] to-[#9B6DFF] text-white px-6 py-3 rounded-xl font-medium hover:shadow-lg hover:-translate-y-[1px] transition-all"
                      onClick={async () => {
                        setError('');
                        try {
                          await savePreferences({ theme: uiTheme });
                          setMessage('Theme saved successfully.');
                        } catch (requestError) {
                          if (requestError.message.toLowerCase().includes('token')) {
                            clearAdminSession();
                          }
                          setError(requestError.message);
                        }
                      }}
                    >
                      Save Theme
                    </button>
                  </div>
                </div>
              ) : null}

              {activePanel === 'invoice' ? (
                <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-[0_6px_20px_rgba(0,0,0,0.06)] p-6 space-y-5">
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                    <h2 className="text-xl font-bold text-[#111827]">Invoice Manager</h2>
                    {invoiceView === 'list' ? (
                      <button
                        type="button"
                        className="bg-gradient-to-r from-[#7C4DFF] to-[#9B6DFF] text-white px-5 py-2 rounded-xl font-medium hover:shadow-lg hover:-translate-y-[1px] transition-all flex items-center gap-2"
                        onClick={() => {
                          setInvoiceForm({ ...initialInvoiceForm, invoiceNumber: `INV-${Date.now().toString().slice(-6)}` });
                          setInvoiceView('create');
                        }}
                      >
                        <Plus className="w-5 h-5" />
                        New Invoice
                      </button>
                    ) : (
                      <button type="button" className="bg-white border border-[#D1D5DB] text-[#4B5563] px-5 py-2 rounded-xl font-medium hover:bg-gray-50 transition-all flex items-center gap-2" onClick={() => setInvoiceView('list')}>
                        <X className="w-5 h-5" />
                        Back to List
                      </button>
                    )}
                  </div>

                  {invoiceView === 'list' ? (
                    <div className="space-y-4">
                      {invoices.length === 0 ? (
                        <div className="py-12 flex flex-col items-center justify-center text-[#6B7280] border-2 border-dashed border-[#E5E7EB] rounded-2xl bg-[#F9FAFB]">
                          <FileText className="w-12 h-12 mb-3 text-[#D1D5DB]" />
                          <p className="text-sm font-medium">No invoices yet. Create your first invoice.</p>
                        </div>
                      ) : (
                        invoices.map((inv) => {
                          const subtotal = inv.items.reduce((s, i) => s + i.quantity * i.rate, 0);
                          const total = subtotal + (subtotal * inv.tax) / 100;
                          return (
                            <div
                              key={inv.invoiceNumber}
                              className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-[#E5E7EB] bg-white p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-md transition-all"
                            >
                              <div>
                                <p className="text-[#111827] font-bold">{inv.invoiceNumber} — <span className="font-medium text-[#4B5563]">{inv.clientName}</span></p>
                                <p className="text-[#6B7280] text-sm mt-1">Due: {inv.dueDate} · <span className="font-semibold text-[#1F2937]">₹{total.toFixed(2)}</span></p>
                              </div>
                              <div className="flex items-center gap-3">
                                <span
                                  className={`text-[11px] px-3 py-1 rounded-full border font-bold ${
                                    inv.status === 'paid'
                                      ? 'text-[#059669] border-[#10B981]/30 bg-[#D1FAE5]'
                                      : 'text-[#D97706] border-[#F59E0B]/30 bg-[#FEF3C7]'
                                  }`}
                                >
                                  {inv.status === 'paid' ? <CheckCircle className="w-3.5 h-3.5 inline mr-1" /> : <Clock className="w-3.5 h-3.5 inline mr-1" />}
                                  {inv.status.toUpperCase()}
                                </span>
                                <button
                                  type="button"
                                  className="bg-white border border-[#D1D5DB] text-[#4B5563] px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 flex items-center justify-center gap-1.5"
                                  onClick={() => { setPreviewInvoice(inv); setInvoiceView('preview'); }}
                                >
                                  <FileText className="w-4 h-4" />
                                  View
                                </button>
                                <button
                                  type="button"
                                  className="bg-[#F3EEFF] text-[#7C4DFF] px-3 py-2 rounded-lg text-sm font-medium hover:bg-[#E9D5FF] flex items-center justify-center gap-1.5"
                                  onClick={() =>
                                    setInvoices((prev) =>
                                      prev.map((i) =>
                                        i.invoiceNumber === inv.invoiceNumber
                                          ? { ...i, status: i.status === 'paid' ? 'unpaid' : 'paid' }
                                          : i
                                      )
                                    )
                                  }
                                >
                                  {inv.status === 'paid' ? 'Mark Unpaid' : 'Mark Paid'}
                                </button>
                                <button
                                  type="button"
                                  className="bg-white border border-[#FCA5A5] text-[#EF4444] px-3 py-2 rounded-lg text-sm font-medium hover:bg-red-50 flex items-center justify-center gap-1.5"
                                  onClick={() =>
                                    setInvoices((prev) => prev.filter((i) => i.invoiceNumber !== inv.invoiceNumber))
                                  }
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  ) : null}

                  {invoiceView === 'create' ? (
                    <form
                      className="space-y-6"
                      onSubmit={(e) => {
                        e.preventDefault();
                        setInvoices((prev) => [{ ...invoiceForm, status: 'unpaid' }, ...prev]);
                        setInvoiceView('list');
                      }}
                    >
                      <div className="grid sm:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-sm font-medium text-[#4B5563] mb-1.5">Client Name</label>
                          <input
                            required
                            type="text"
                            placeholder="John Doe"
                            value={invoiceForm.clientName}
                            onChange={(e) => setInvoiceForm((p) => ({ ...p, clientName: e.target.value }))}
                            className="w-full h-[46px] px-4 rounded-xl bg-white border border-[#D1D5DB] text-[#1F2937] focus:border-[#7C4DFF] focus:ring-1 focus:ring-[#7C4DFF] outline-none transition-shadow"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-[#4B5563] mb-1.5">Client Email</label>
                          <input
                            type="email"
                            placeholder="john@example.com"
                            value={invoiceForm.clientEmail}
                            onChange={(e) => setInvoiceForm((p) => ({ ...p, clientEmail: e.target.value }))}
                            className="w-full h-[46px] px-4 rounded-xl bg-white border border-[#D1D5DB] text-[#1F2937] focus:border-[#7C4DFF] focus:ring-1 focus:ring-[#7C4DFF] outline-none transition-shadow"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-[#4B5563] mb-1.5">Invoice Number</label>
                          <input
                            required
                            type="text"
                            placeholder="INV-000001"
                            value={invoiceForm.invoiceNumber}
                            onChange={(e) => setInvoiceForm((p) => ({ ...p, invoiceNumber: e.target.value }))}
                            className="w-full h-[46px] px-4 rounded-xl bg-white border border-[#D1D5DB] text-[#1F2937] focus:border-[#7C4DFF] focus:ring-1 focus:ring-[#7C4DFF] outline-none transition-shadow bg-gray-50"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-[#4B5563] mb-1.5">Due Date</label>
                          <input
                            required
                            type="date"
                            value={invoiceForm.dueDate}
                            onChange={(e) => setInvoiceForm((p) => ({ ...p, dueDate: e.target.value }))}
                            className="w-full h-[46px] px-4 rounded-xl bg-white border border-[#D1D5DB] text-[#1F2937] focus:border-[#7C4DFF] focus:ring-1 focus:ring-[#7C4DFF] outline-none transition-shadow"
                          />
                        </div>
                      </div>

                      <div className="space-y-3 bg-[#F9FAFB] p-5 rounded-2xl border border-[#E5E7EB]">
                        <p className="text-sm font-bold text-[#111827]">Line Items</p>
                        {invoiceForm.items.map((item, idx) => (
                          <div key={idx} className="grid grid-cols-[1fr_80px_120px_36px] gap-3 items-center">
                            <input
                              required
                              type="text"
                              placeholder="Description of service/product"
                              value={item.description}
                              onChange={(e) =>
                                setInvoiceForm((p) => {
                                  const items = [...p.items];
                                  items[idx] = { ...items[idx], description: e.target.value };
                                  return { ...p, items };
                                })
                              }
                              className="h-[42px] px-3 rounded-lg bg-white border border-[#D1D5DB] text-[#1F2937] focus:border-[#7C4DFF] focus:ring-1 focus:ring-[#7C4DFF] outline-none text-sm w-full"
                            />
                            <input
                              required
                              type="number"
                              min="1"
                              placeholder="Qty"
                              value={item.quantity}
                              onChange={(e) =>
                                setInvoiceForm((p) => {
                                  const items = [...p.items];
                                  items[idx] = { ...items[idx], quantity: Number(e.target.value) };
                                  return { ...p, items };
                                })
                              }
                              className="h-[42px] px-3 rounded-lg bg-white border border-[#D1D5DB] text-[#1F2937] focus:border-[#7C4DFF] focus:ring-1 focus:ring-[#7C4DFF] outline-none text-sm w-full"
                            />
                            <input
                              required
                              type="number"
                              min="0"
                              placeholder="Rate ₹"
                              value={item.rate}
                              onChange={(e) =>
                                setInvoiceForm((p) => {
                                  const items = [...p.items];
                                  items[idx] = { ...items[idx], rate: Number(e.target.value) };
                                  return { ...p, items };
                                })
                              }
                              className="h-[42px] px-3 rounded-lg bg-white border border-[#D1D5DB] text-[#1F2937] focus:border-[#7C4DFF] focus:ring-1 focus:ring-[#7C4DFF] outline-none text-sm w-full"
                            />
                            <button
                              type="button"
                              onClick={() =>
                                setInvoiceForm((p) => ({ ...p, items: p.items.filter((_, i) => i !== idx) }))
                              }
                              disabled={invoiceForm.items.length === 1}
                              className="text-[#EF4444] hover:text-[#DC2626] disabled:opacity-30 p-2"
                            >
                              <X className="w-5 h-5" />
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          className="bg-white border border-[#D1D5DB] text-[#4B5563] px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 flex items-center gap-1.5 mt-2"
                          onClick={() =>
                            setInvoiceForm((p) => ({
                              ...p,
                              items: [...p.items, { description: '', quantity: 1, rate: 0 }],
                            }))
                          }
                        >
                          <Plus className="w-4 h-4" />
                          Add Item
                        </button>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-sm font-medium text-[#4B5563] mb-1.5">Tax (%)</label>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={invoiceForm.tax}
                            onChange={(e) => setInvoiceForm((p) => ({ ...p, tax: Number(e.target.value) }))}
                            className="w-full h-[46px] px-4 rounded-xl bg-white border border-[#D1D5DB] text-[#1F2937] focus:border-[#7C4DFF] focus:ring-1 focus:ring-[#7C4DFF] outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-[#4B5563] mb-1.5">Notes</label>
                          <input
                            type="text"
                            placeholder="Payment terms, bank details..."
                            value={invoiceForm.notes}
                            onChange={(e) => setInvoiceForm((p) => ({ ...p, notes: e.target.value }))}
                            className="w-full h-[46px] px-4 rounded-xl bg-white border border-[#D1D5DB] text-[#1F2937] focus:border-[#7C4DFF] focus:ring-1 focus:ring-[#7C4DFF] outline-none"
                          />
                        </div>
                      </div>

                      <div className="text-right text-sm text-[#4B5563] space-y-1 bg-[#F9FAFB] p-4 rounded-xl border border-[#E5E7EB]">
                        {(() => {
                          const subtotal = invoiceForm.items.reduce((s, i) => s + i.quantity * i.rate, 0);
                          const tax = (subtotal * invoiceForm.tax) / 100;
                          return (
                            <>
                              <p>Subtotal: <span className="font-medium text-[#111827]">₹{subtotal.toFixed(2)}</span></p>
                              <p>Tax ({invoiceForm.tax}%): <span className="font-medium text-[#111827]">₹{tax.toFixed(2)}</span></p>
                              <p className="text-[#7C4DFF] font-bold text-lg pt-2 border-t border-[#E5E7EB] mt-2">Total: ₹{(subtotal + tax).toFixed(2)}</p>
                            </>
                          );
                        })()}
                      </div>

                      <button type="submit" className="bg-gradient-to-r from-[#7C4DFF] to-[#9B6DFF] text-white px-6 py-3 rounded-xl font-medium hover:shadow-lg hover:-translate-y-[1px] transition-all flex items-center justify-center gap-2 w-full">
                        <FileText className="w-5 h-5" />
                        Save Invoice
                      </button>
                    </form>
                  ) : null}

                  {invoiceView === 'preview' && previewInvoice ? (() => {
                    const subtotal = previewInvoice.items.reduce((s, i) => s + i.quantity * i.rate, 0);
                    const tax = (subtotal * previewInvoice.tax) / 100;
                    const total = subtotal + tax;
                    return (
                      <div className="space-y-4">
                        <div
                          id="invoice-preview"
                          className="invoice-sheet print:rounded-none print:border-none print:shadow-none"
                        >
                          <div className="invoice-sheet__header">
                            <div className="invoice-sheet__brand-wrap">
                              <div className="invoice-sheet__logo-box">
                                <img src={companyLogo} alt="CLIENTCODE AI" className="invoice-sheet__logo" />
                              </div>
                              <div>
                                <h3 className="invoice-sheet__brand">CLIENTCODE AI INNOVATION PRIVATE LIMITED</h3>
                                <p className="invoice-sheet__brand-sub">First Floor-102, SRS Corporate Tower, Sector 31, Faridabad, Haryana 121003</p>
                              </div>
                            </div>
                            <div className="invoice-sheet__meta">
                              <p className="invoice-sheet__title-tag">Tax Invoice</p>
                              <p><span>Invoice No.</span> <strong>{previewInvoice.invoiceNumber}</strong></p>
                              <p><span>Issue Date</span> <strong>{previewInvoice.issueDate}</strong></p>
                              <p><span>Due Date</span> <strong>{previewInvoice.dueDate || '-'}</strong></p>
                            </div>
                          </div>

                          <div className="invoice-sheet__body">
                            <div className="invoice-sheet__party-grid">
                              <div className="invoice-sheet__party-card">
                                <p className="invoice-sheet__section-label">From - Seller Details</p>
                                <p className="invoice-sheet__party-name">CLIENTCODE AI INNOVATION PRIVATE LIMITED</p>
                                <p className="invoice-sheet__party-sub">support@clientcodeai.in</p>
                                <p className="invoice-sheet__party-sub">+91 95822 92739</p>
                              </div>

                              <div className="invoice-sheet__party-card">
                                <p className="invoice-sheet__section-label">Bill To - Client Details</p>
                                <p className="invoice-sheet__party-name">{previewInvoice.clientName || '-'}</p>
                                <p className="invoice-sheet__party-sub">{previewInvoice.clientEmail || '-'}</p>
                              </div>
                            </div>

                            <table className="invoice-sheet__table">
                              <thead>
                                <tr>
                                  <th className="text-left">Description</th>
                                  <th className="text-right">Qty</th>
                                  <th className="text-right">Rate</th>
                                  <th className="text-right">Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {previewInvoice.items.map((item, i) => (
                                  <tr key={i}>
                                    <td>{item.description || '-'}</td>
                                    <td className="text-right">{item.quantity}</td>
                                    <td className="text-right">₹{item.rate.toFixed(2)}</td>
                                    <td className="text-right">₹{(item.quantity * item.rate).toFixed(2)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>

                            <div className="invoice-sheet__totals-wrap">
                              <div className="invoice-sheet__totals-card">
                                <div className="invoice-sheet__total-line">
                                  <span>Subtotal</span>
                                  <strong>₹{subtotal.toFixed(2)}</strong>
                                </div>
                                <div className="invoice-sheet__total-line">
                                  <span>Tax ({previewInvoice.tax}%)</span>
                                  <strong>₹{tax.toFixed(2)}</strong>
                                </div>
                                <div className="invoice-sheet__total-line invoice-sheet__total-line--grand">
                                  <span>Total</span>
                                  <strong>₹{total.toFixed(2)}</strong>
                                </div>
                              </div>
                            </div>

                            {previewInvoice.notes ? (
                              <p className="invoice-sheet__notes">{previewInvoice.notes}</p>
                            ) : null}
                          </div>

                          <div className="invoice-sheet__footer">This is a computer-generated invoice. No signature required.</div>
                        </div>
                        <button
                          type="button"
                          className="bg-gradient-to-r from-[#7C4DFF] to-[#9B6DFF] text-white px-6 py-3 rounded-xl font-medium hover:shadow-lg hover:-translate-y-[1px] transition-all flex items-center justify-center gap-2 no-print w-full sm:w-auto"
                          onClick={() => window.print()}
                        >
                          <Download className="w-5 h-5" />
                          Download / Print PDF
                        </button>
                      </div>
                    );
                  })() : null}
                </div>
              ) : null}

              {activePanel === 'salary-slip' ? (
                <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-[0_6px_20px_rgba(0,0,0,0.06)] p-6 space-y-5">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <h2 className="text-xl font-bold text-[#111827]">Salary Slip Generator</h2>
                    <a
                      href="/salary-slip-generator.html"
                      target="_blank"
                      rel="noreferrer"
                      className="bg-white border border-[#D1D5DB] text-[#4B5563] px-5 py-2 rounded-xl font-medium hover:bg-gray-50 transition-all flex items-center gap-2"
                    >
                      Open In New Tab
                    </a>
                  </div>
                  <div className="rounded-xl overflow-hidden border border-[#E5E7EB] bg-[#F9FAFB] h-[78vh] min-h-[640px] shadow-inner">
                    <iframe
                      title="Salary Slip Generator"
                      src="/salary-slip-generator.html"
                      className="w-full h-full"
                    />
                  </div>
                </div>
              ) : activePanel === 'internships' ? (
                <div className="text-slate-800">
                  <AdminCertificates />
                </div>
              ) : activePanel === 'contact-enquiries' ? (
                <div className="text-slate-800">
                  <AdminContactEnquiries />
                </div>
              ) : null}
            </motion.div>
          </main>
        </div>

      {/* Compact Post Form Modal */}
      {createPortal(
        <AnimatePresence>
          {showCreatePost && (
            <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh' }}>
              <motion.div 
                initial={{ opacity: 0, scale: 0.95, y: 10 }} 
                animate={{ opacity: 1, scale: 1, y: 0 }} 
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden"
              >
                <div className="flex justify-between items-center p-4 border-b border-[#E5E7EB] bg-[#F9FAFB]">
                  <h3 className="font-bold text-[#111827] flex items-center gap-2 text-sm">
                    <PlusSquare className="w-4 h-4 text-[#7C4DFF]" /> 
                    {editingPostId ? 'Edit Post' : 'Create New Post'}
                  </h3>
                  <button onClick={() => { setShowCreatePost(false); resetPostForm(); }} className="text-gray-400 hover:text-gray-600 transition-colors">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                
                <form onSubmit={handleSubmit} className="p-4 space-y-3">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-[#4B5563] mb-1">Platform</label>
                      <select
                        value={formData.platform}
                        onChange={(event) => setFormData((prev) => ({ ...prev, platform: event.target.value }))}
                        className="w-full h-[38px] px-3 rounded-lg bg-white border border-[#D1D5DB] text-[#1F2937] focus:border-[#7C4DFF] focus:ring-1 focus:ring-[#7C4DFF] outline-none text-sm transition-shadow"
                      >
                        <option>Instagram</option>
                        <option>LinkedIn</option>
                        <option>Facebook</option>
                        <option>Twitter/X</option>
                        <option>YouTube</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-[#4B5563] mb-1">Scheduled Date</label>
                      <input
                        type="datetime-local"
                        required
                        value={formData.scheduledAt}
                        onChange={(event) => setFormData((prev) => ({ ...prev, scheduledAt: event.target.value }))}
                        className="w-full h-[38px] px-3 rounded-lg bg-white border border-[#D1D5DB] text-[#1F2937] focus:border-[#7C4DFF] focus:ring-1 focus:ring-[#7C4DFF] outline-none text-sm transition-shadow"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-[#4B5563] mb-1">Caption</label>
                    <textarea
                      rows={3}
                      required
                      value={formData.caption}
                      onChange={(event) => setFormData((prev) => ({ ...prev, caption: event.target.value }))}
                      placeholder="Write caption..."
                      className="w-full p-3 rounded-lg bg-white border border-[#D1D5DB] text-[#1F2937] focus:border-[#7C4DFF] focus:ring-1 focus:ring-[#7C4DFF] outline-none text-sm transition-shadow resize-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-[#4B5563] mb-1">Hashtags</label>
                    <input
                      type="text"
                      value={formData.hashtags}
                      onChange={(event) => setFormData((prev) => ({ ...prev, hashtags: event.target.value }))}
                      placeholder="#ai, #automation"
                      className="w-full h-[38px] px-3 rounded-lg bg-white border border-[#D1D5DB] text-[#1F2937] focus:border-[#7C4DFF] focus:ring-1 focus:ring-[#7C4DFF] outline-none text-sm transition-shadow"
                    />
                  </div>

                  <label className="flex flex-col items-center justify-center border-2 border-dashed border-[#D1D5DB] rounded-lg p-4 cursor-pointer hover:bg-[#F9FAFB] transition-colors group">
                    <ImagePlus className="w-5 h-5 text-[#9CA3AF] group-hover:text-[#7C4DFF] mb-1.5 transition-colors" />
                    <span className="text-xs font-semibold text-[#4B5563] mb-0.5">Upload Media</span>
                    <span className="text-[10px] text-[#6B7280]">PNG, JPG up to 5MB</span>
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/gif"
                      className="hidden"
                      onChange={(event) => setSelectedImage(event.target.files?.[0] || null)}
                    />
                    {selectedImage ? <p className="text-xs text-[#7C4DFF] mt-2 font-medium bg-[#F3EEFF] px-2 py-1 rounded-md truncate max-w-xs">{selectedImage.name}</p> : null}
                  </label>

                  <div className="flex gap-3 pt-2">
                    <button 
                      type="button" 
                      onClick={() => { setShowCreatePost(false); resetPostForm(); }}
                      className="w-1/3 border border-[#D1D5DB] text-[#4B5563] hover:bg-gray-50 py-2 rounded-lg text-sm font-semibold transition-colors"
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      disabled={loading}
                      className="flex-1 bg-gradient-to-r from-[#7C4DFF] to-[#9B6DFF] hover:shadow-lg text-white py-2 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {loading ? <CalendarClock className="w-4 h-4 animate-pulse" /> : <Send className="w-4 h-4" />}
                      {loading ? 'Saving...' : editingPostId ? 'Update Post' : 'Schedule Post'}
                    </button>
                  </div>
                </form>
              </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

export default AdminSocialPost;